import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { forkJoin, Subscription } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { HttpResponse } from '@angular/common/http';
import { Header } from '../../components/shared/header/header';
import { SummaryCard } from '../../components/summary-card/summary-card';
import { SummaryCardData } from '../../components/summary-card/summary-card';
import { SummaryBlockData, SummaryBlocks } from '../../components/summary-blocks/summary-blocks';
import { VerticalBarChart } from '../../components/vertical-bar-chart/vertical-bar-chart';
import { DonutChart } from '../../components/donut-chart/donut-chart';
import { DashboardService } from '../../services/dashboard-service.service';
import { CardMask } from '../../components/card-mask/card-mask';
import { HomeMetricasResponse } from '../../interfaces/home-metricas.interface';
import { HomeGraficoMensalResponse } from '../../interfaces/home-grafico-mensal.interface';
import { HomeGraficoPercentualResponse } from '../../interfaces/home-grafico-percentual.interface';
import { LeadHistoricoResumo } from '../../interfaces/lead-historico.interface';
import { LeadHistoricoService } from '../../services/lead-historico.service';
import { SelectedDnService } from '../../services/selected-dn.service';
import { DashboardDateSelector } from '../../dashboard-date-selector/dashboard-date-selector';
import { ExportDocumentButton } from '../../export-document-button/export-document-button';
import { ExportDownloadModal } from '../../components/export-download-modal/export-download-modal';
import { ConfigService } from '../../services/config.service';
import { SummaryCardMobile } from '../../components/summary-card-mobile/summary-card-mobile';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    Header,
    SummaryCard,
    SummaryBlocks,
    VerticalBarChart,
    DonutChart,
    SummaryCardMobile,
    CardMask,
    DashboardDateSelector,
    ExportDocumentButton,
    ExportDownloadModal
  ],
  templateUrl: './dashboard.html',
  styleUrls: ['./dashboard.scss']
})
export class Dashboard implements OnInit, OnDestroy {
  showExportModal = false;
  exportFileName = '';
  exportFileBlob: Blob | null = null;
  exportEmailSubject = '';
  exportEmailBody = '';
  private dnSubscription?: Subscription;
  private initialLoadDone = false;

  // Dados da API
  apiLeadsData: any[] = [];

  // Dados das novas APIs
  metricasData: HomeMetricasResponse | null = null;
  graficoMensalData: HomeGraficoMensalResponse | null = null;
  graficoPercentualData: HomeGraficoPercentualResponse | null = null;
  leadHistoricoResumoData: LeadHistoricoResumo | null = null;

  // Estados de loading e erro
  loadingMetricas = false;
  loadingGraficos = false;
  errorMetricas: string | null = null;
  errorGraficos: string | null = null;
  currentStartDate: string = '';
  currentEndDate: string = '';
  isExporting = false;

  // Máscaras: gráficos agora sem máscara
  readonly chartsHaveMask: boolean = false;

  // Cards que terão máscara (índices do array cards)
  private readonly maskedCards: number[] = [];

  // Modal data
  infoModalData = {
    isOpen: false,
    title: '',
    message: ''
  };

  constructor(
    private dashboardService: DashboardService,
    private leadHistoricoService: LeadHistoricoService,
    private selectedDnService: SelectedDnService,
    private configService: ConfigService
  ) {}

  ngOnInit() {
    if (this.configService.isDebugEnabled()) {
      console.log('Dashboard - ngOnInit');
    }

    this.dnSubscription = this.selectedDnService.selectedDn$.subscribe(dn => {
      if (this.configService.isDebugEnabled()) {
        console.log('Dashboard - DN changed:', dn);
      }
      if (dn && this.initialLoadDone) {
        this.loadAllData();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.dnSubscription) {
      this.dnSubscription.unsubscribe();
    }
  }

  onDateRangeChange(dateRange: {start: string, end: string}): void {
    if (this.configService.isDebugEnabled()) {
      console.log('Dashboard - onDateRangeChange:', dateRange);
    }

    this.currentStartDate = dateRange.start;
    this.currentEndDate = dateRange.end;

    if (!this.initialLoadDone) {
      this.initialLoadDone = true;
      this.loadAllData();
    } else {
      this.loadAllData();
    }
  }

  onExportLeadsRequested(): void {
    if (this.isExporting || !this.currentStartDate || !this.currentEndDate) {
      return;
    }

    const codigoDn = this.getCodigoDn();
    if (!codigoDn) {
      console.warn('Dashboard - Export cancelado: DN não encontrado.');
      return;
    }

    this.isExporting = true;

    this.dashboardService
      .exportHomeLeadsExcel(codigoDn, this.currentStartDate, this.currentEndDate)
      .subscribe({
        next: (response) => {
          const fileName = this.extractFileNameFromResponse(response) || this.buildFallbackExportFileName();
          const fileBlob = response.body ?? new Blob();
          this.exportFileName = fileName;
          this.exportFileBlob = fileBlob;
          const formatDate = (dateStr: string) => {
            const d = new Date(dateStr);
            const day = String(d.getDate()).padStart(2, '0');
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const year = d.getFullYear();
            return `${day}/${month}/${year}`;
          };
          const startDateFmt = formatDate(this.currentStartDate);
          const endDateFmt = formatDate(this.currentEndDate);
          this.exportEmailSubject = `Relatório de Leads (${startDateFmt} a ${endDateFmt})`;
          this.exportEmailBody = `Olá,\n\nSegue em anexo o relatório de leads referente ao período de ${startDateFmt} até ${endDateFmt}.\n\nAtenciosamente,\nEquipe Volkswagen Financial Services`;
          this.showExportModal = true;
          this.isExporting = false;
        },
        error: (error) => {
          console.error('Erro ao exportar leads do dashboard:', error);
          this.isExporting = false;
        }
      });
  }

  handleExportModalClose = () => {
    this.showExportModal = false;
  };

  handleExportModalDownload = () => {
    if (this.exportFileBlob && this.exportFileName) {
      this.triggerFileDownload(this.exportFileBlob, this.exportFileName);
    }
  };

  get exportStartYear(): number | null {
    return this.getYearFromDate(this.currentStartDate);
  }

  get exportStartMonth(): number | null {
    return this.getMonthFromDate(this.currentStartDate);
  }

  get exportEndYear(): number | null {
    return this.getYearFromDate(this.currentEndDate);
  }

  get exportEndMonth(): number | null {
    return this.getMonthFromDate(this.currentEndDate);
  }

  private getYearFromDate(dateValue: string): number | null {
    if (!dateValue) return null;
    const parsedDate = new Date(dateValue);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate.getFullYear();
  }

  private getMonthFromDate(dateValue: string): number | null {
    if (!dateValue) return null;
    const parsedDate = new Date(dateValue);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate.getMonth() + 1;
  }

  private extractFileNameFromResponse(response: HttpResponse<Blob>): string | null {
    const contentDisposition = response.headers.get('content-disposition');
    if (!contentDisposition) return null;

    const utf8FileNameMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8FileNameMatch?.[1]) return decodeURIComponent(utf8FileNameMatch[1]);

    const defaultFileNameMatch = contentDisposition.match(/filename="?([^\";]+)"?/i);
    return defaultFileNameMatch?.[1] ?? null;
  }

  private buildFallbackExportFileName(): string {
    const months = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
    const formatPeriodDate = (dateStr: string): string => {
      const d = new Date(dateStr);
      const dd = String(d.getDate()).padStart(2, '0');
      const mon = months[d.getMonth()];
      const yy = String(d.getFullYear()).slice(-2);
      return `${dd}${mon}${yy}`;
    };
    const now = new Date();
    const dd = String(now.getDate()).padStart(2, '0');
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const yy = String(now.getFullYear()).slice(-2);
    const hh = String(now.getHours()).padStart(2, '0');
    const min = String(now.getMinutes()).padStart(2, '0');
    const ss = String(now.getSeconds()).padStart(2, '0');
    const startPart = this.currentStartDate ? formatPeriodDate(this.currentStartDate) : 'inicio';
    const endPart = this.currentEndDate ? formatPeriodDate(this.currentEndDate) : 'fim';
    const timestamp = `${dd}${mm}${yy}${hh}${min}${ss}`;
    return `FSLeads_Export_${startPart}_${endPart}_${timestamp}.xlsx`;
  }

  private triggerFileDownload(blob: Blob, fileName: string): void {
    if (typeof window === 'undefined') return;
    const objectUrl = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = fileName;
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.URL.revokeObjectURL(objectUrl);
  }

  private loadAllData(): void {
    if (this.configService.isDebugEnabled()) {
      console.log('Dashboard - loadAllData with dates:', {
        start: this.currentStartDate,
        end: this.currentEndDate
      });
    }
    this.loadLeadsData();
    this.loadLeadHistoricoResumoData();
    this.loadHomeData();
  }

  private getCodigoDn(): string {
    return this.selectedDnService.getSelectedDn();
  }

  loadLeadsData() {
    const codigoDn = this.getCodigoDn();
    this.dashboardService.getLeadsHomeSegmentos(codigoDn, this.currentStartDate, this.currentEndDate).subscribe({
      next: (response) => {
        if (response && response.length > 0) {
          this.apiLeadsData = response;
        } else {
          this.apiLeadsData = [];
        }
      },
      error: (error) => {
        console.error('Erro ao carregar dados:', error);
      }
    });
  }

  loadLeadHistoricoResumoData() {
    const codigoDn = this.getCodigoDn();
    this.leadHistoricoService.getLeadHistoricoResumo(codigoDn, this.currentStartDate, this.currentEndDate).subscribe({
      next: (response) => {
        if (response) {
          this.leadHistoricoResumoData = response;
        } else {
          this.leadHistoricoResumoData = null;
        }
      },
      error: (error) => {
        console.error('Erro ao carregar dados de lead historico resumo:', error);
      }
    });
  }

  loadHomeData() {
    const codigoDn = this.getCodigoDn();

    this.loadingMetricas = true;
    this.errorMetricas = null;

    this.dashboardService.getHomeMetricas(codigoDn, this.currentStartDate, this.currentEndDate).subscribe({
      next: (response) => {
        if (response) {
          this.metricasData = response;
          this.loadingMetricas = false;
        } else {
          this.metricasData = null;
          this.loadingMetricas = false;
        }
      },
      error: (error) => {
        console.error('Erro ao carregar métricas:', error);
        this.errorMetricas = this.getErrorMessage(error);
        this.loadingMetricas = false;
      }
    });

    this.loadingGraficos = true;
    this.errorGraficos = null;

    forkJoin({
      mensal: this.dashboardService.getHomeGraficoMensal(codigoDn).pipe(catchError(error => {
        console.error('Erro ao carregar gráfico mensal:', error);
        return of(null);
      })),
      percentual: this.dashboardService.getHomeGraficoPercentual(codigoDn, this.currentStartDate, this.currentEndDate).pipe(catchError(error => {
        console.error('Erro ao carregar gráfico percentual:', error);
        return of(null);
      }))
    }).subscribe({
      next: (response) => {
        this.graficoMensalData = response.mensal;
        this.graficoPercentualData = response.percentual;
        this.loadingGraficos = false;
      },
      error: (error) => {
        console.error('Erro ao carregar gráficos:', error);
        this.errorGraficos = this.getErrorMessage(error);
        this.loadingGraficos = false;
      }
    });
  }

  private getErrorMessage(error: any): string {
    if (error.status === 400) return 'DN ausente ou inválido';
    if (error.status === 401) return 'Token inválido ou expirado';
    if (error.status === 403) return 'Usuário sem permissão para DN';
    if (error.status === 500) return 'Erro no servidor';
    return 'Erro ao carregar dados';
  }

  shouldHaveMask(cardIndex: number): boolean {
    return this.maskedCards.includes(cardIndex);
  }

  shouldHaveMaskSummaryBlock(blockIndex: number): boolean {
    return blockIndex >= 3;
  }

  calculateTrabalhadoPercent(): number {
    if (!this.metricasData) return 0;
    const disponibilizados = this.metricasData.disponibilizados || 0;
    const pendentesNovos = this.metricasData.pendentesNovos || 0;
    if (disponibilizados === 0) return 0;
    const trabalhados = disponibilizados - pendentesNovos;
    return (trabalhados / disponibilizados) * 100;
  }

  calculateNaoTrabalhadoPercent(): number {
    if (!this.metricasData) return 0;
    const disponibilizados = this.metricasData.disponibilizados || 0;
    const pendentesNovos = this.metricasData.pendentesNovos || 0;
    if (disponibilizados === 0) return 0;
    return (pendentesNovos / disponibilizados) * 100;
  }

  get cards(): SummaryCardData[] {
    const dataMap = new Map();
    this.apiLeadsData.forEach(item => {
      dataMap.set(item.idTipoCampanha, item);
    });

    const signDrivePendentes = this.sumSignDriveLeads('totalLeadsPendentesNovos');
    const signDriveNegociacao = this.sumSignDriveLeads('totalLeadsEmNegociacao');

    const subscriptionRenewPendentes = this.sumSubscriptionRenewLeads('totalLeadsPendentesNovos');
    const subscriptionRenewNegociacao = this.sumSubscriptionRenewLeads('totalLeadsEmNegociacao');


    const comingSoon = this.configService.isPhase2Enabled();

    return [
      {
        title: "Simulações Online",
        subtitle: "Financiamento",
        icon: "assets/icons/iconOnlineSimulation.png",
        iconBackgroundColor: "#4CC7F4",
        borderColor: "#001E50",
        items: [
          { name: "Pendentes/Novos", value: dataMap.get(1)?.totalLeadsPendentesNovos || 0 },
          { name: "Em Negociação", value: dataMap.get(1)?.totalLeadsEmNegociacao || 0 },
        ],
        buttonText: "Acessar",
        buttonRoute: "/simulations",
        tooltipText: "Leads originados pelos simuladores de financiamento online como: Ofertas VW e Sempre Novo.",
        trackEvent: "simulacoes_online",
        trackCategory: "dashboard",
        trackLabel: "Simulações Online"
      },
      {
        title: "Propostas Aprovadas",
        subtitle: "Financiamento",
        icon: "assets/icons/iconApprovedProposal.png",
        iconBackgroundColor: "#7DC5AB",
        borderColor: "#001E50",
        items: [
          { name: "Pendentes/Novos", value: dataMap.get(2)?.totalLeadsPendentesNovos || 0 },
          { name: "Em Negociação", value: dataMap.get(2)?.totalLeadsEmNegociacao || 0 },
        ],
        buttonText: "Acessar",
        buttonRoute: "/approved-proposals",
        tooltipText: "Propostas aprovadas há mais de 31 dias sem conversão em contrato",
        trackEvent: "approved_proposals_card",
        trackCategory: "dashboard",
        trackLabel: "Propostas Aprovadas"
      },
      {
        title: "Período de Recompra",
        subtitle: "Financiamento",
        icon: "assets/icons/iconRebuyingPeriod.png",
        iconBackgroundColor: "#EEA776",
        borderColor: "#001E50",
        items: [
          { name: "Pendentes/Novos", value: dataMap.get(3)?.totalLeadsPendentesNovos || 0 },
          { name: "Em Negociação", value: dataMap.get(3)?.totalLeadsEmNegociacao || 0 },
        ],
        buttonText: "Acessar",
        buttonRoute: "/rebuying",
        tooltipText: "Clientes em período de recompra que estão a três meses do encerramento do contrato, ou liquidado antecipadamente.",
        trackEvent: "periodo_recompra",
        trackCategory: "dashboard",
        trackLabel: "Período de Recompra"
      },
      {
        title: "Campanhas Especiais",
        subtitle: "Financiamento",
        icon: "assets/icons/iconCampaigns.png",
        iconBackgroundColor: "#FFE87F",
        borderColor: "#001E50",
        items: [
          { name: "Pendentes/Novos", value: dataMap.get(4)?.totalLeadsPendentesNovos || 0 },
          { name: "Em Negociação", value: dataMap.get(4)?.totalLeadsEmNegociacao || 0 },
        ],
        buttonText: "Acessar",
        buttonRoute: "/campaigns",
        tooltipText: "Clientes segmentados por campanhas de financiamento criadas pela VWFS de acordo com definições estratégicas de marketing",
        trackEvent: "campaigns_card",
        trackCategory: "dashboard",
        trackLabel: "Campanhas Especiais",
        comingSoon
      },
      {
        title: "Simulações Online",
        subtitle: "VW Sign&Drive",
        icon: "assets/icons/iconSignDrive.png",
        iconBackgroundColor: "#00B0F0",
        borderColor: "#001E50",
        items: [
          { name: "Pendentes/Novos", value: signDrivePendentes },
          { name: "Em Negociação", value: signDriveNegociacao },
        ],
        buttonText: "Acessar",
        buttonRoute: "/sign-drive",
        tooltipText: "Leads originados pelos simuladores de assinatura online como: VW Sign&Drive e CoBranded",
        trackEvent: "sign_drive_card",
        trackCategory: "dashboard",
        trackLabel: "Sign&Drive",
      },
      {
        title: "Renovações",
        subtitle: "VW Sign&Drive",
        icon: "assets/icons/iconRenewal.png",
        iconBackgroundColor: "#00B0F0",
        borderColor: "#001E50",
        items: [
          { name: "Pendentes/Novos", value: subscriptionRenewPendentes },
          { name: "Em Negociação", value: subscriptionRenewNegociacao },
        ],
        buttonText: "Acessar",
        buttonRoute: "/subscription-renew",
        tooltipText: "Clientes no período de renovação, que estão em período de encerramento do contrato.",
        trackEvent: "subscription_renew_card",
        trackCategory: "dashboard",
        trackLabel: "Renovações Assinaturas"
      },
      {
        title: "Cotas Contempladas",
        icon: "assets/icons/iconQuotas.png",
        iconBackgroundColor: "#c5c07d",
        borderColor: "#001E50",
        items: [
          { name: "Pendentes/Novos", value: dataMap.get(9)?.totalLeadsPendentesNovos || 0 },
          { name: "Em Negociação", value: dataMap.get(9)?.totalLeadsEmNegociacao || 0 },
        ],
        buttonText: "Acessar",
        buttonRoute: "/quotas",
        tooltipText: "Clientes que tiveram sua cota de consórcio contemplada",
        trackEvent: "quotas_card",
        trackCategory: "dashboard",
        trackLabel: "Cotas contempladas"
      },
      // {
      //   title: "Seguros & Serviços",
      //   icon: "assets/icons/iconInsurance.png",
      //   iconBackgroundColor: "#7DC5AB",
      //   borderColor: "#001E50",
      //   items: [
      //     { name: "Pendentes/Novos", value: dataMap.get(10)?.totalLeadsPendentesNovos || 0 },
      //     { name: "Em Negociação", value: dataMap.get(10)?.totalLeadsEmNegociacao || 0 },
      //   ],
      //   buttonText: "Acessar",
      //   buttonRoute: "/insurance-services",
      //   tooltipText: "Campanhas especiais de produtos e serviços agregados",
      //   trackEvent: "insurance_services_card",
      //   trackCategory: "dashboard",
      //   trackLabel: "Seguros & Serviços",
      //   comingSoon
      // },
      {
        title: "Histórico de Leads",
        icon: "assets/icons/iconHistory.png",
        iconBackgroundColor: "#ECD9B3",
        borderColor: "#7E7E7E",
        items: [
          { name: "Vendas Efetivadas", value: this.leadHistoricoResumoData?.vendasEfetivadas || 0 },
          { name: "Vendas Perdidas", value: this.leadHistoricoResumoData?.vendasPerdidas || 0 },
        ],
        buttonText: "Acessar",
        buttonRoute: "/leads-history",
        tooltipText: "Lista de leads disponibilizados.",
        trackEvent: "historico_leads",
        trackCategory: "dashboard",
        trackLabel: "Histórico de Leads"
      }
    ];
  }

  sumSignDriveLeads(field: string): number {
    return this.apiLeadsData
      .filter(item => [5].includes(item.idTipoCampanha))
      .reduce((sum, item) => sum + (item[field] || 0), 0);
  }

  sumSubscriptionRenewLeads(field: string): number {
    return this.apiLeadsData
      .filter(item => [6].includes(item.idTipoCampanha))
      .reduce((sum, item) => sum + (item[field] || 0), 0);
  }

  get summaryBlocks(): SummaryBlockData[] {
    return [
      {
        title: "Leads Recebidos",
        bgColor: "#00B0F0",
        number: this.metricasData?.disponibilizados ?? 0,
        icon: "assets/icons/iconCorrect.png",
        tooltipText: "Leads disponibilizados para atendimento"
      },
      {
        title: "Leads Pendentes",
        bgColor: "#57737D",
        number: this.metricasData?.pendentesNovos ?? 0,
        icon: "assets/icons/iconNew.png",
        tooltipText: "Leads pendentes de contato"
      },
      {
        title: "Conversão",
        bgColor: "#001E50",
        number: this.metricasData?.conversaoMediaPercentual ? `${this.metricasData.conversaoMediaPercentual.toFixed(0)}%` : "0%",
        icon: "assets/icons/iconConversion.png",
        tooltipText: "Contrato assinado"
      },
      {
        title: "Brand Renewal",
        bgColor: "#ffffff",
        number: this.metricasData?.brandRenewalMediaPercentual ? `${this.metricasData.brandRenewalMediaPercentual.toFixed(0)}%` : "0%",
        icon: "assets/icons/iconRenewal.png"
      }
    ];
  }

  openInfoModal(data: {title: string, message: string}) {
    this.infoModalData = {
      isOpen: true,
      title: data.title,
      message: data.message
    };
    document.body.style.overflow = 'hidden';
  }

  closeInfoModal() {
    this.infoModalData.isOpen = false;
    document.body.style.overflow = 'auto';
  }

  onChartInfoClick(title: string, message: string): void {
    if (typeof window !== 'undefined' && window.innerWidth <= 1024) {
      this.openInfoModal({ title, message });
    }
  }
}
